// Single mutable state object. Modules import it and mutate directly —
// rendering functions read from it. Keeps the data flow obvious and
// debuggable without a framework.

// `localStorage` and `navigator` are browser-only; guard so the module is
// importable in node-side tooling (e.g. SSR / linters / build-time review).
const HAS_LS  = typeof localStorage !== 'undefined';
const HAS_NAV = typeof navigator    !== 'undefined';

const LS = (k, d) => {
  if (!HAS_LS) return d;
  try { const v = localStorage.getItem(k); return v == null ? d : v; }
  catch { return d; }
};
const LSn = (k, d) => {
  const v = LS(k, null);
  if (v == null) return d;
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const LSjson = (k, d) => {
  if (!HAS_LS) return d;
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; }
  catch { return d; }
};

// Detect a browser locale that we actually translate; fall back to 'en'.
const SUPPORTED_LOCALES = ['en','es','fr','de','it','pt','nl'];
const detectLocale = () => {
  const raw = HAS_NAV ? ((navigator.language || 'en').toLowerCase().slice(0, 2)) : 'en';
  return SUPPORTED_LOCALES.includes(raw) ? raw : 'en';
};

// Loginless anonymous identity. We mint a stable per-browser UUID and feed it
// into the Hype-Lab mock-auth signup as `anon_<uuid>@trut.hu`. The backend
// cookie then becomes the only thing tying this browser to its persona,
// credits, and OAuth state — no Supabase / no user-visible login.
const anonEmail = (() => {
  if (!HAS_LS) return null;
  try {
    let id = localStorage.getItem('hybrid.anonId');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : (Date.now().toString(36) + Math.random().toString(36).slice(2, 10));
      localStorage.setItem('hybrid.anonId', id);
    }
    return 'anon_' + id.replace(/-/g, '') + '@trut.hu';
  } catch { return null; }
})();

export const state = {
  // Bootstrap (filled by main.js at boot from /api/lab/config)
  boot: null,           // {model_catalog, default_model, hook_styles, tweet_max_chars, credits_per_usd}

  // Session — loginless. Populated at boot by main.js::bootAnonSession from
  // the Hype-Lab `/api/lab/me` response (which is tied to a browser cookie
  // we establish via a synthetic anonymous signup). No Supabase.
  me: null,             // { id, email, name, credits, x_profile_image_url, x_display_name, ... }
  persona: null,        // {id, name, voice_description, has_oauth, oauth_x_username, ...}

  // Stable per-browser anonymous identity (UUID-derived email). Used to
  // hand the Hype-Lab backend a stable handle so personas + credits survive
  // page reloads in the same browser. Nothing leaves the browser unless the
  // user connects X (which the backend handles).
  anonEmail,

  // Tour
  step: 1,              // 1..5
  kind: 'replies',      // 'replies' | 'posts'
  kindCount: 20,
  selectedHooks: [],
  sourceType: null,     // 'user' | 'list' | 'tweet'
  sourceId: null,

  // Generation tracking
  previewJobId: null,
  mainJobId: null,
  previewDone: false,
  mainDone: false,
  pollTimer: null,
  seenOutputIds: new Set(),
  currentOutputs: [],
  currentSourceTweets: [],
  currentJob: null,
  recentJobs: [],

  // Misc
  voiceTimer: null,

  // --- v2 additions (funnel + i18n + paywall amount + stickiness) ---

  // UI + reply localisation. UI strings honour `locale`; the reply-generation
  // engine honours `replyLanguage` (sent as `options.language` to the backend).
  locale:        LS('hybrid.locale', detectLocale()),
  replyLanguage: LS('hybrid.replyLang', LS('hybrid.locale', detectLocale())),

  // Paywall — picked amount in whole USD. Default $10 anchors above the $5
  // minimum and unlocks the +20% bonus tier.
  selectedTopupAmount: LSn('hybrid.topup', 10),

  // Which landing variant the user first hit. Persisted so the OAuth
  // round-trip (which leaves the SPA and returns) doesn't reset it.
  landingVariant: LS('hybrid.entryVariant', 'default'), // 'default'|'suspension'|'viral'|'variations'

  // Quick-mode funnel — defaults to true; flipped off when the user clicks
  // "Advanced" in the tour. Hidden behind `?tour=1` for full re-runs.
  quickMode: true,

  // Stickiness — cached last source + last job for the 24-h follow-up banner
  // and the "Reply again to <list>" CTA on landing for returning users.
  lastSource:     LSjson('hybrid.lastSource', null), // { type, id, name }
  lastJobAt:      LSn('hybrid.lastJobAt', 0),        // epoch ms
  lastJobId:      LS('hybrid.lastJobId', null),
};
